import { useState, useEffect, useCallback } from 'react';
import { useFilterConditionAtomValue } from '@/features/analyze/atoms/filterCondition.atom';
import { NodeSingular } from 'cytoscape';
import { useNetworkState } from '@/features/connect-dots/hooks/useNetworkState';

/**
 * ノードの選択状態と関連情報を管理するフック
 */
export const useNodeSelection = () => {
  const { cytoscapeCoreRefObject } = useNetworkState();

  // フィルター条件の取得
  const filterCondition = useFilterConditionAtomValue();

  // 選択されたノードの完全なデータ
  const [selectedNodeSingulars, setSelectedNodeSingulars] = useState<
    NodeSingular[] | null
  >(null);

  // 選択されたノードのIDリスト
  const [selectedNodeIds, setSelectedNodeIds] = useState<Set<string>>(
    new Set<string>()
  );

  /**
   * ノードを選択するアクション
   */
  const addSelectedNodeSingular = (node: NodeSingular | null) => {
    if (!cytoscapeCoreRefObject) return;
    if (node) {
      const selectedCyNode = cytoscapeCoreRefObject.getElementById(
        node.data().id
      );
      if (selectedCyNode.length) {
        selectedCyNode.addClass('selected');
      } else {
        selectedCyNode.removeClass('selected');

        const selectedNodeArray = [...(selectedNodeSingulars || []), node];
        setSelectedNodeSingulars(selectedNodeArray);
      }
    }
  };

  /**
   * ノード選択を解除するアクション
   */
  const removeSelectedNodeSingular = (node: NodeSingular | null) => {
    if (!cytoscapeCoreRefObject) return;
    if (node) {
      const selectedCyNode = cytoscapeCoreRefObject.getElementById(
        node.data().id
      );
      if (selectedCyNode.length) {
        selectedCyNode.removeClass('selected');

        const selectedNodeArray = [...(selectedNodeSingulars || []), node];
        setSelectedNodeSingulars(selectedNodeArray);
      }
    }
  };

  const selectNodeById = useCallback(
    (nodeId: string | null) => {
      if (
        cytoscapeCoreRefObject == null ||
        cytoscapeCoreRefObject == null ||
        nodeId == null
      )
        return;

      const selectedCyNode = cytoscapeCoreRefObject.getElementById(nodeId);
      if (selectedCyNode.length) {
        selectedCyNode.addClass('selected');

        const selectedNodeArray = [
          ...(selectedNodeSingulars || []),
          selectedCyNode,
        ];
        setSelectedNodeSingulars(selectedNodeArray);
      }
    },
    [cytoscapeCoreRefObject, selectedNodeSingulars]
  );

  const removeNodeById = (nodeId: string | null) => {
    if (
      cytoscapeCoreRefObject == null ||
      cytoscapeCoreRefObject == null ||
      nodeId == null
    )
      return;
    const selectedCyNode = cytoscapeCoreRefObject.getElementById(nodeId);
    if (selectedCyNode.length) {
      selectedCyNode.removeClass('selected');
      setSelectedNodeSingulars(null);
    }
  };

  /**
   * 選択をクリアする
   */
  const clearSelection = useCallback(() => {
    if (!cytoscapeCoreRefObject) return;

    // すべてのノードから選択状態を解除
    cytoscapeCoreRefObject.elements().removeClass('selected');

    setSelectedNodeSingulars(null);
  }, [cytoscapeCoreRefObject]);

  useEffect(() => {
    const newSelectedItemIDs: string[] = [];
    if (filterCondition) {
      // selectedNodeIdsに、重複のないタグIDを追加
      if (Array.isArray(filterCondition.topicTags?.or)) {
        if (Array.isArray(filterCondition.topicTags?.or)) {
          newSelectedItemIDs.push(
            ...filterCondition.topicTags.or.map((tag) => {
              // タグのIDを生成
              const tagId = `topic_tag_${tag.replace(/\s+/g, '_').toLowerCase()}`;
              return tagId;
            })
          );
        }
      }
      if (Array.isArray(filterCondition.topicTags?.and)) {
        newSelectedItemIDs.push(
          ...filterCondition.topicTags.and.map((tag) => {
            // タグのIDを生成
            const tagId = `topic_tag_${tag.replace(/\s+/g, '_').toLowerCase()}`;
            return tagId;
          })
        );
      }
      if (Array.isArray(filterCondition.topicTags?.not)) {
        newSelectedItemIDs.push(
          ...filterCondition.topicTags.not.map((tag) => {
            // タグのIDを生成
            const tagId = `topic_tag_${tag.replace(/\s+/g, '_').toLowerCase()}`;
            return tagId;
          })
        );
      }

      if (Array.isArray(filterCondition.themes?.or)) {
        newSelectedItemIDs.push(
          ...filterCondition.themes.or.map((tag) => {
            // タグのIDを生成
            const tagId = `theme_tag_${tag.replace(/\s+/g, '_').toLowerCase()}`;
            return tagId;
          })
        );
      }
      if (Array.isArray(filterCondition.themes?.and)) {
        newSelectedItemIDs.push(
          ...filterCondition.themes.and.map((tag) => {
            // タグのIDを生成
            const tagId = `theme_tag_${tag.replace(/\s+/g, '_').toLowerCase()}`;
            return tagId;
          })
        );
      }

      // 他にも filterCondition に含まれうるタグ関連のフィールドがあればここに追加
    }
    // オブジェクトに変換することで重複を排除
    const uniqueIds = [...new Set(newSelectedItemIDs)];

    clearSelection();

    uniqueIds.forEach((id) => {
      // 各IDに対して選択状態を更新
      selectNodeById(id);
    });

    // 選択されたノードのIDを更新
    setSelectedNodeIds(new Set(newSelectedItemIDs));
  }, [filterCondition, clearSelection, selectNodeById]);

  return {
    selectedNodeSingulars,
    addSelectedNodeSingular,
    removeSelectedNodeSingular,
    selectedNodeIds,
    addSelectedNodeById: selectNodeById,
    removeSelectedNodeById: removeNodeById,
    clearSelection,
  };
};
